import type { ThemeOverrideChannels } from '@bridebook/web/lib/wedding-website/motif/resolve-css';
import {
  IAddress,
  IDatePicker,
  IWedding,
  IWedding_Task,
  IWedding_Website,
  IWedding_Website_Section,
} from '@bridebook/models/source/models/Weddings.types';
import { mapDateToUI } from '@bridebook/toolbox';
import { ICollaborator } from '@bridebook/web/lib/access-control/types';
import {
  IWeddingWebsiteDesignTabsState,
  IWeddingWebsiteOnboardingState,
} from '@bridebook/web/lib/wedding-website/types';
import { TTimestampedSerializedRecursive } from '@bridebook/toolbox/src/serialize-timestamps';
import { ICost } from '@bridebook/models/source/models/Weddings/Costs.types';
import { ITask } from '@bridebook/models/source/models/Weddings/Tasks.types';
import type { ISupplier as IDBWeddingSupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { OmitMutableTimestampsRecursive } from '@bridebook/web/lib/utils';

export type UIDate = ReturnType<typeof mapDateToUI>;

// Wedding-date / location value shapes shared by onboarding steps and other wedding-detail UIs.
export type WeddingLocationType = IAddress | null;
export type WeddingYearType = number;

export type TWeddingSerialized = TTimestampedSerializedRecursive<
  OmitMutableTimestampsRecursive<IWedding, ['draft']>
>;
export type TWeddingTaskSerialized = TTimestampedSerializedRecursive<IWedding_Task>;
export type TCostSerialized = TTimestampedSerializedRecursive<
  OmitMutableTimestampsRecursive<ICost>
>;
export type TTaskSerialized = TTimestampedSerializedRecursive<ITask>;
export type TWeddingSupplierSerialized = TTimestampedSerializedRecursive<
  OmitMutableTimestampsRecursive<IDBWeddingSupplier>
>;

export interface IProfile
  extends Omit<OmitMutableTimestampsRecursive<TWeddingSerialized>, 'tasks'> {
  id: string;
  date?: IDatePicker;
  tasks: TWeddingTaskSerialized;
  engagementDateDatePicker?: IDatePicker;
  weddingDate?: string;
}

export interface IWeddingVenue {
  name: string;
  collection: 'suppliers';
  publicID?: string;
  id: string;
  town: string;
}

export interface ILatLongBounds {
  ne: { lat: number; lon: number };
  sw: { lat: number; lon: number };
}

export interface IWeddingWebsiteDataState {
  design: IWeddingWebsiteDesignTabsState;
  dirty: boolean;
  draft: {
    wedding: {
      partners: [string, string];
      weddingDate: IDatePicker;
    };
    website: IWedding_Website;
  };
  onboarding: IWeddingWebsiteOnboardingState;
  shouldPrefill: boolean;
  updatedAt: number;
  showFetchDraftModal: boolean;
  motif: {
    /**
     * Client-only echo of the couple's latest palette/font taps, applied inline on `.bb-ww` so a tap
     * recolours the live preview INSTANTLY with no CSS refetch. The theme `css` is the source of truth
     * (a tap burns into it); cleared when fresh CSS is served and on conversation switch. Null = none.
     */
    previewOverride: ThemeOverrideChannels | null;
    /** Count of in-flight burn POSTs; a chat send / publish waits for 0 before reading the draft css.
     * See pending-burns.ts. */
    pendingBurns: number;
    /**
     * Just-prompted themes, id → sentAt ms. The server only writes a `motifThemes` entry once the agent
     * calls the generate tool, so a gallery placeholder stands in for each id here until then.
     */
    pendingGenerations: Record<string, number>;
  };
}

export interface IWeddingsState {
  profile: IProfile;
  collaborators: Array<ICollaborator>;
  loaded: boolean;
  countryLatLongBounds: ILatLongBounds;
  listenersInitialised: boolean;
  inboxUnread: number;
  loading: boolean;
  websiteMeta: IWeddingWebsiteDataState;
}

export enum PartnerRoles {
  bride = 'bride',
  groom = 'groom',
  other = 'other',
}

type WithoutVisible<T> = T extends { visible: boolean } ? Omit<T, 'visible'> : T;

export type WebsiteSectionUpdate = WithoutVisible<IWedding_Website_Section>;
